import { useEffect, useRef, useState, type ChangeEvent, type CSSProperties, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDestinations, useUploadCatalog } from '../../api/hooks';
import { ApiError } from '../../api/client';
import { Spinner } from '../ui/Spinner';
import { UploadChannelPicker } from './UploadChannelPicker';
import { joinNames } from './channelDisplay';
import { darkTheme, FONT } from '../../theme/tokens';
import {
  welcomeCopy,
  MAX_UPLOAD_BYTES,
  PROGRESS_HINT_THRESHOLD_BYTES,
  ACCEPTED_FILE_EXTENSIONS,
} from '../../copy/welcome';

const t = darkTheme;

function formatBytes(b: number): string {
  // The bytes branch exists because the staged card names EVERY file, not just
  // the >=5MB ones the progress hint used to be limited to: a 46-byte CSV
  // rendered as "0.0 KB", which reads as an empty or broken file.
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(filename: string): string {
  const dotIdx = filename.lastIndexOf('.');
  if (dotIdx === -1) return '';
  return filename.slice(dotIdx).toLowerCase();
}

function FolderIcon({ size = 48, color = t.text2 }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function FileIcon({ size = 48, color = t.accent }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}

function CheckIcon({ size = 48, color = t.accent }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12l5 5L20 6" />
    </svg>
  );
}

const ctaStyle: CSSProperties = {
  padding: '10px 24px',
  borderRadius: 8,
  border: `1px solid ${t.accentBorder}`,
  background: 'transparent',
  color: t.accent,
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background 150ms ease',
};

const subtleButtonStyle: CSSProperties = {
  padding: '10px 14px',
  borderRadius: 8,
  border: '1px solid transparent',
  background: 'transparent',
  color: t.text2,
  fontFamily: FONT,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

/**
 * `staged` is the state this screen was missing (SC-3148).
 *
 * The card used to fire the upload the instant a file was chosen. That made the
 * channel picker unreachable in practice — channels would have had to be picked
 * BEFORE the drop, which is backwards from how a drop zone is used — and it left
 * the error path with a better state model than the happy path, because "your
 * file is still here, change something and resubmit" only existed after a 422.
 *
 * Staging is one click for a user who doesn't care about channels, and in
 * exchange the failure path stops being a special case: a failed upload is just
 * `staged` again with a message on it, and the button stays `Upload` — a form
 * resubmit, honest for every failure — rather than a "Try again" that can't
 * change a malformed CSV.
 */
type Phase = 'idle' | 'staged' | 'uploading' | 'success';

interface CatalogUploadProps {
  variant?: 'card' | 'inline';
}

export function CatalogUpload({ variant = 'card' }: CatalogUploadProps) {
  const navigate = useNavigate();
  const upload = useUploadCatalog();
  const {
    data: destinations,
    refetch: refetchDestinations,
    isError: destinationsFailed,
    isFetching: destinationsFetching,
  } = useDestinations(variant === 'card');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [dragDepth, setDragDepth] = useState(0);
  const [phase, setPhase] = useState<Phase>('idle');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [pickedSlugs, setPickedSlugs] = useState<string[]>([]);
  const [newCatalogId, setNewCatalogId] = useState<string | null>(null);

  const isInline = variant === 'inline';
  const isDragging = dragDepth > 0;
  // A file can be chosen at rest AND while one is staged — dropping a second file
  // replaces the first rather than being ignored.
  const canPickFile = phase === 'idle' || phase === 'staged';

  // The channel picker is a card-variant affordance only (the query is disabled
  // for inline, above). The inline button lives in the app chrome with nowhere to
  // put a staged file or a disclosure panel, so it keeps the original fire-on-pick
  // behaviour and sends no membership.
  //
  // A failed load is said out loud rather than rendered as "no picker": hidden,
  // it reads exactly like an org with no channels, and the user uploads without
  // the membership they meant to assign. Membership is NOT reliably settable
  // afterwards — upload runs under CREATE_CATALOG, the table's bulk assign under
  // `manage_destination_membership` — so this screen may be their only chance.
  // Quiet and non-blocking: skipping channels is still a fine answer.
  const channels = destinations ?? [];
  const channelsUnavailable = destinationsFailed && destinations === undefined;

  // Hold on `success` for a beat so the check icon registers, then navigate
  // to the new catalog's workspace (the entities page, which hosts the Profile
  // tab where analysis lands). useEffect (not setTimeout in onSuccess) guarantees
  // cleanup on unmount.
  //
  // The id check is `=== null` not `!newCatalogId` — empty-string from a
  // misbehaving API would falsy-pass the latter and silently strand the user
  // on the success screen with no navigation. With a strict null check, an
  // empty id navigates to /catalogs//entities and lands on the existing
  // "Catalog not found" state (which is at least diagnosable).
  useEffect(() => {
    if (phase !== 'success' || newCatalogId === null) return;
    const id = window.setTimeout(() => navigate(`/catalogs/${newCatalogId}/entities`), 350);
    return () => window.clearTimeout(id);
  }, [phase, newCatalogId, navigate]);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_UPLOAD_BYTES) return welcomeCopy.errors.tooLarge;
    const ext = getExtension(file.name);
    if (!ACCEPTED_FILE_EXTENSIONS.includes(ext as typeof ACCEPTED_FILE_EXTENSIONS[number])) {
      return welcomeCopy.errors.wrongType;
    }
    return null;
  };

  const runUpload = (file: File, slugs: string[]) => {
    upload.reset();
    setValidationError(null);
    setStagedFile(file);
    setPhase('uploading');
    upload.mutate(
      // Empty stays `undefined` rather than `[]`: both append nothing to the form,
      // but the intent — "no membership was asked for" — is worth keeping legible
      // at the call site.
      { file, destinationSlugs: slugs.length > 0 ? slugs : undefined },
      {
        onSuccess: (catalog) => {
          setNewCatalogId(catalog.id);
          setPhase('success');
        },
        onError: () => {
          // The card keeps the file staged so a retry is one click and not a
          // re-drag — of a file that may be 100MB. The inline button has nowhere
          // to show a staged file, so it returns to rest.
          if (isInline) {
            setStagedFile(null);
            setPhase('idle');
          } else {
            setPhase('staged');
          }
        },
      },
    );
  };

  /**
   * Submit the staged file, after checking the picked channels still exist.
   *
   * An unknown slug fails the WHOLE upload with a 422 before a single row lands,
   * so a channel deleted between page load and submit costs the user the entire
   * file. One cheap GET closes most of that window.
   *
   * Only when channels were actually picked: the common first-run path is "no
   * channels", and it must not grow a network round trip or a second way to fail.
   */
  const submitStaged = async () => {
    const file = stagedFile;
    if (!file) return;
    upload.reset();
    setValidationError(null);

    if (pickedSlugs.length === 0) {
      runUpload(file, []);
      return;
    }

    setPhase('uploading');
    const fresh = await refetchDestinations();
    // Fall back to the cached list if the refetch itself failed, and skip the
    // check entirely if we have no list at all — pruning a user's selection
    // because a secondary GET hiccuped would be worse than the 422, which at
    // least fails closed with nothing written.
    const list = fresh.data ?? destinations;
    if (list) {
      const known = new Set(list.map((d) => d.slug));
      const missing = pickedSlugs.filter((s) => !known.has(s));
      if (missing.length > 0) {
        // Named from the STALE list — the whole point is that they're gone from
        // the fresh one — falling back to the slug if even that can't name them.
        const names = missing.map(
          (slug) => destinations?.find((d) => d.slug === slug)?.name ?? slug,
        );
        const one = missing.length === 1;
        setPickedSlugs((prev) => prev.filter((s) => known.has(s)));
        setValidationError(
          `${joinNames(names)} ${one ? 'is' : 'are'} no longer available, so nothing was uploaded. ` +
            `${one ? 'It’s' : 'They’ve'} been unchecked — ${file.name} is still selected.`,
        );
        setPhase('staged');
        return;
      }
      // Every picked slug survived; send the pruned-by-nothing list as-is. NOT
      // filtered down to the fresh list's members — a partially resolved set is
      // exactly what the backend refuses to guess at, and quietly dropping one
      // here would commit the same sin one layer up.
    }

    runUpload(file, pickedSlugs);
  };

  /** A chosen file: rejected outright, staged, or (inline) sent immediately. */
  const acceptFile = (file: File) => {
    const err = validateFile(file);
    if (err) {
      // Deliberately does NOT stage: re-submitting a 200MB file or a .png fails
      // identically, so there is nothing here for a retry to change. It also
      // UNSTAGES whatever was there: the user's last act was "replace this file",
      // and leaving the old one primed behind an enabled Upload would send a file
      // they just tried to swap out.
      upload.reset();
      setStagedFile(null);
      if (!isInline) setPhase('idle');
      setValidationError(err);
      return;
    }
    upload.reset();
    setValidationError(null);
    if (isInline) {
      runUpload(file, []);
      return;
    }
    setStagedFile(file);
    setPhase('staged');
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) acceptFile(file);
    // Reset input so the same file can be picked again after an error.
    e.target.value = '';
  };

  // Drag depth counter — onDragLeave fires when crossing into child elements,
  // so a plain boolean would flicker. Counting enter/leave keeps state stable.
  const handleDragEnter = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    if (canPickFile) setDragDepth((d) => d + 1);
  };

  const handleDragOver = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragDepth((d) => Math.max(0, d - 1));
  };

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragDepth(0);
    if (!canPickFile) return;
    const file = e.dataTransfer.files?.[0];
    if (file) acceptFile(file);
  };

  const triggerFilePicker = () => {
    if (canPickFile) fileInputRef.current?.click();
  };

  const toggleSlug = (slug: string) => {
    setPickedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  const uploadErrorMessage = upload.error
    ? upload.error instanceof ApiError
      ? upload.error.detail
      : welcomeCopy.errors.generic
    : null;
  const errorMessage = validationError ?? uploadErrorMessage;

  if (isInline) {
    return (
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept={welcomeCopy.upload.accept}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={triggerFilePicker}
          disabled={phase !== 'idle'}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: `1px solid ${isDragging ? t.accent : t.border}`,
            background: isDragging ? t.accentBg : 'transparent',
            color: t.text,
            fontFamily: FONT,
            fontSize: 13,
            fontWeight: 500,
            cursor: phase === 'idle' ? 'pointer' : 'wait',
            transition: 'border 200ms ease, background 200ms ease',
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {phase === 'uploading' ? 'Uploading…' : '+ Upload catalog'}
        </button>
        {errorMessage && (
          <div style={{ color: t.red, fontSize: 12, marginTop: 6 }}>{errorMessage}</div>
        )}
      </div>
    );
  }

  // card variant
  const cardBorder = isDragging ? `2px solid ${t.accent}` : `2px dashed ${t.border2}`;
  const cardShadow = isDragging ? `0 0 24px rgba(104, 202, 145, 0.25)` : 'none';
  const iconScale = isDragging ? 1.05 : 1;

  // The progress line keys off the MUTATION, not the phase: `uploading` also
  // covers the destinations re-check that runs before the POST, and saying
  // "Uploading catalog.csv" while nothing has been sent yet would be a lie.
  const showProgressHint =
    upload.isPending && stagedFile !== null && stagedFile.size >= PROGRESS_HINT_THRESHOLD_BYTES;

  const bodyText = showProgressHint
    ? `${welcomeCopy.uploading} ${stagedFile.name} (${formatBytes(stagedFile.size)})…`
    : phase === 'staged' && stagedFile
      ? `${stagedFile.name} · ${formatBytes(stagedFile.size)}`
      : welcomeCopy.upload.body;

  return (
    <div
      style={{
        opacity: phase === 'success' ? 0 : 1,
        transition: 'opacity 250ms ease',
      }}
    >
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={triggerFilePicker}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          // Only the card's OWN Enter/Space opens the file dialog. Without this
          // guard, Space on a focused button inside the card bubbles up here, gets
          // preventDefault'd, and opens a file dialog instead of pressing the
          // button — which would make Upload unreachable by keyboard.
          if (e.target !== e.currentTarget) return;
          if ((e.key === 'Enter' || e.key === ' ') && canPickFile) {
            e.preventDefault();
            triggerFilePicker();
          }
        }}
        style={{
          background: t.surface,
          border: cardBorder,
          borderRadius: 12,
          padding: '40px 32px',
          textAlign: 'center',
          cursor: canPickFile ? 'pointer' : 'default',
          transition: 'border 200ms ease, box-shadow 200ms ease',
          boxShadow: cardShadow,
          userSelect: 'none',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={welcomeCopy.upload.accept}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          aria-hidden="true"
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 16,
            transform: `scale(${iconScale})`,
            transition: 'transform 150ms ease',
          }}
        >
          {phase === 'success' ? (
            <CheckIcon />
          ) : phase === 'uploading' ? (
            <Spinner size={48} />
          ) : phase === 'staged' ? (
            <FileIcon />
          ) : (
            <FolderIcon />
          )}
        </div>
        <h3 style={{ color: t.text, margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>
          {welcomeCopy.upload.title}
        </h3>
        <p style={{ color: t.text2, fontSize: 14, lineHeight: 1.5, margin: '0 auto 20px', maxWidth: 380, wordBreak: 'break-word' }}>
          {bodyText}
        </p>

        {phase === 'idle' && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              triggerFilePicker();
            }}
            style={ctaStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = t.accentBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {welcomeCopy.upload.cta}
          </button>
        )}

        {phase === 'staged' && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* stopPropagation on both: the whole card is a file-dialog click
                target, so without it pressing Upload would also open a file
                dialog on top of the upload it just started. */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void submitStaged();
              }}
              style={ctaStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = t.accentBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Upload
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                triggerFilePicker();
              }}
              style={subtleButtonStyle}
            >
              Choose a different file
            </button>
          </div>
        )}

        {errorMessage && (
          <div role="alert" style={{ color: t.red, fontSize: 13, marginTop: 16 }}>
            {errorMessage}
          </div>
        )}
        {/* Subtle drag hint when not actively dragging — only on card variant */}
        {phase === 'idle' && !errorMessage && (
          <div style={{ color: t.text3, fontSize: 11, marginTop: 14 }}>
            {welcomeCopy.upload.dragHint}
          </div>
        )}
      </div>

      {/* Outside the drop zone, not inside it: the card swallows Enter/Space and
          clicks to open the file dialog, and a checkbox living under that would
          toggle nothing while popping a file dialog instead. */}
      {channelsUnavailable && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: t.text3, textAlign: 'left' }}>
          Couldn’t load your channels, so you can’t pick one right now.{' '}
          <button
            type="button"
            onClick={() => {
              void refetchDestinations();
            }}
            disabled={destinationsFetching}
            style={{
              padding: 0,
              border: 'none',
              background: 'transparent',
              color: t.accent,
              fontFamily: FONT,
              fontSize: 12.5,
              fontWeight: 500,
              cursor: destinationsFetching ? 'default' : 'pointer',
            }}
          >
            {destinationsFetching ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}

      {channels.length > 0 && (
        <UploadChannelPicker
          destinations={channels}
          selected={pickedSlugs}
          onToggle={toggleSlug}
          disabled={phase === 'uploading'}
        />
      )}
    </div>
  );
}
